import React, { useState, useMemo } from 'react'
import {
  Table,
  Header,
  Button,
  SpaceBetween,
  Box,
  Pagination,
  TextFilter,
  CollectionPreferences,
  Flashbar,
  FlashbarProps
} from '@cloudscape-design/components'

interface SelectableTableProps<T = any> {
  items: T[]
  columnDefinitions: any[]
  loading?: boolean
  loadingText?: string
  selectionType?: 'single' | 'multi'
  selectedItems?: T[]
  onSelectionChange?: (selection: T[]) => void
  header?: string
  description?: string
  actions?: React.ReactNode
  empty?: React.ReactNode
  filter?: {
    placeholder?: string
    filteringFunction?: (item: T, query: string) => boolean
  }
  pagination?: {
    pageSize?: number
  }
  sortingDisabled?: boolean
  preferences?: {
    title?: string
    confirmLabel?: string
    cancelLabel?: string
    pageSizeOptions?: { value: number; label: string }[]
    visibleContentOptions?: { label: string; options: { id: string; label: string }[] }[]
  }
  notifications?: FlashbarProps.MessageDefinition[]
}

export default function SelectableTable<T = any>({
  items,
  columnDefinitions,
  loading,
  loadingText = "Loading...",
  selectionType = 'single',
  selectedItems = [],
  onSelectionChange,
  header,
  description,
  actions,
  empty,
  filter,
  pagination,
  sortingDisabled = false,
  preferences,
  notifications = []
}: SelectableTableProps<T>) {
  const [filterText, setFilterText] = useState('')
  const [currentPageIndex, setCurrentPageIndex] = useState(1)
  const [pageSize, setPageSize] = useState(pagination?.pageSize || 10)
  const [sortingColumn, setSortingColumn] = useState<any>()
  const [sortingDescending, setSortingDescending] = useState(false)
  const [columnPreferences, setColumnPreferences] = useState({
    pageSize: pagination?.pageSize || 10,
    visibleContent: columnDefinitions.map(col => col.id)
  })

  // Filter items based on search text
  const filteredItems = useMemo(() => {
    if (!filterText.trim()) return items
    
    if (filter?.filteringFunction) {
      return items.filter(item => filter.filteringFunction!(item, filterText))
    }
    
    // Default filtering: search in all string fields
    return items.filter(item => 
      Object.values(item).some(value => 
        String(value).toLowerCase().includes(filterText.toLowerCase())
      )
    )
  }, [items, filterText, filter?.filteringFunction])

  // Sort items
  const sortedItems = useMemo(() => {
    if (!sortingColumn || sortingDisabled) return filteredItems
    
    return [...filteredItems].sort((a, b) => {
      const aVal = a[sortingColumn.sortingField || sortingColumn.id]
      const bVal = b[sortingColumn.sortingField || sortingColumn.id]
      
      let result = 0
      if (aVal < bVal) result = -1
      if (aVal > bVal) result = 1
      
      return sortingDescending ? -result : result
    })
  }, [filteredItems, sortingColumn, sortingDescending, sortingDisabled])

  // Paginate items
  const paginatedItems = useMemo(() => {
    if (!pagination) return sortedItems
    
    const startIndex = (currentPageIndex - 1) * pageSize
    const endIndex = startIndex + pageSize
    return sortedItems.slice(startIndex, endIndex)
  }, [sortedItems, currentPageIndex, pageSize, pagination])

  // Visible column definitions based on preferences
  const visibleColumnDefinitions = useMemo(() => {
    return columnDefinitions.filter(col => 
      columnPreferences.visibleContent.includes(col.id)
    )
  }, [columnDefinitions, columnPreferences.visibleContent])

  const defaultEmpty = (
    <Box textAlign="center" color="inherit">
      <b>No items found</b>
      <Box variant="p" color="inherit">
        {filterText ? 'No items match the current filter.' : 'No items to display.'}
      </Box>
    </Box>
  )

  return (
    <SpaceBetween size="m">
      {notifications.length > 0 && <Flashbar items={notifications} />}
      
      <Table
        items={paginatedItems}
        columnDefinitions={visibleColumnDefinitions}
        loading={loading}
        loadingText={loadingText}
        selectionType={selectionType}
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => onSelectionChange?.(detail.selectedItems)}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn)
          setSortingDescending(detail.isDescending || false)
        }}
        sortingDisabled={sortingDisabled}
        header={
          <Header
            counter={`(${items.length})`}
            actions={actions}
            description={description}
          >
            {header}
          </Header>
        }
        filter={filter && (
          <TextFilter
            filteringText={filterText}
            filteringPlaceholder={filter.placeholder || "Search items..."}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            countText={`${filteredItems.length} ${filteredItems.length === 1 ? 'match' : 'matches'}`}
          />
        )}
        pagination={pagination && (
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={Math.ceil(filteredItems.length / pageSize)}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        )}
        preferences={preferences && (
          <CollectionPreferences
            title={preferences.title || "Preferences"}
            confirmLabel={preferences.confirmLabel || "Confirm"}
            cancelLabel={preferences.cancelLabel || "Cancel"}
            preferences={columnPreferences}
            onConfirm={({ detail }) => {
              setColumnPreferences(detail)
              setPageSize(detail.pageSize || pageSize)
              setCurrentPageIndex(1)
            }}
            pageSizePreference={{
              title: "Page size",
              options: preferences.pageSizeOptions || [
                { value: 10, label: "10 items" },
                { value: 20, label: "20 items" },
                { value: 50, label: "50 items" }
              ]
            }}
            visibleContentPreference={{
              title: "Select visible content",
              options: preferences.visibleContentOptions || [
                {
                  label: "Main properties",
                  options: columnDefinitions.map(col => ({
                    id: col.id,
                    label: col.header
                  }))
                }
              ]
            }}
          />
        )}
        empty={empty || defaultEmpty}
      />
    </SpaceBetween>
  )
}